import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { HoverCard, TooltipGrid, TooltipItem, TooltipSection } from '@/components/common/HoverCard';
import { formatNumber } from '@/utils/format';
import { riskDashboardApi } from '@/api/services/riskDashboard';
import type {
  RiskDashboardClientAccountEnrichmentDto,
  RiskDashboardFilters,
} from '@/api/types/riskDashboard';
import type { MergedLpRow } from './AccountsTab';

function num(v: number | null | undefined, digits = 2): string {
  return v == null || Number.isNaN(v) ? 'N/A' : formatNumber(v, digits);
}

const InfoButton = (
  <button type="button" className="fk-info-btn" title="Analytics">
    <i className="fe fe-info" />
  </button>
);

// --- Instrument profitability (lazy fetch on hover, cached by symbol+filters) ---

export function InstrumentInfoTooltip({ symbol, filters }: { symbol: string; filters: RiskDashboardFilters }) {
  return (
    <HoverCard
      width={480}
      title={
        <span>
          <i className="fe fe-bar-chart-2 me-2 text-primary" />
          {symbol} <span className="fs-11 text-muted fw-normal">· Instrument profitability</span>
        </span>
      }
      body={<InstrumentBody symbol={symbol} filters={filters} />}
    >
      {InfoButton}
    </HoverCard>
  );
}

function InstrumentBody({ symbol, filters }: { symbol: string; filters: RiskDashboardFilters }) {
  const q = useQuery({
    queryKey: [
      'rd-instr-prof',
      symbol,
      filters.startDate,
      filters.endDate,
      filters.liveMode,
      filters.platformId,
      filters.login,
      filters.uniqueId,
    ],
    queryFn: () =>
      riskDashboardApi.getInstrumentProfitability({
        instrument: symbol,
        isLive: !!filters.liveMode,
        startDate: filters.startDate,
        endDate: filters.endDate,
        connectorId: filters.platformId,
        login: filters.login,
        uniqueId: filters.uniqueId,
        showLp: filters.showLp,
      }),
    staleTime: 60_000,
  });

  if (q.isLoading) {
    return (
      <p className="text-muted mb-0 fs-12">
        <i className="fa fa-spinner fa-spin me-1" />
        Loading instrument profitability…
      </p>
    );
  }
  if (q.isError) {
    // Legacy risk-dashboard-instrument-tooltip.js: 403/401 → permission message.
    const status = axios.isAxiosError(q.error) ? q.error.response?.status : undefined;
    const msg =
      status === 403 || status === 401
        ? 'Missing Instrument Profitability permission.'
        : 'Failed to load instrument profitability.';
    return <p className="text-danger mb-0 fs-12">{msg}</p>;
  }

  const items = q.data?.items ?? [];
  const d = items.find((i) => (i.symbol ?? '').toUpperCase() === symbol.toUpperCase()) ?? items[0];
  if (!d) {
    return (
      <p className="text-muted mb-0 fs-12">
        No profitability data for <strong>{symbol}</strong> today.
      </p>
    );
  }

  const total = (d.numberOfProfitTrades || 0) + (d.numberOfLossTrades || 0);
  const winRate = total > 0 ? `${((d.numberOfProfitTrades / total) * 100).toFixed(1)}%` : '—';

  return (
    <>
      <TooltipGrid>
        <TooltipItem label="STP / B-Book" value={d.stpBBook || '—'} />
        <TooltipItem label="Connector" value={d.connector || '—'} />
        <TooltipItem label="Total PnL" value={num(d.totalPnl)} />
        <TooltipItem label="Avg PnL" value={num(d.averagePnl)} />
        <TooltipItem label="Win Rate" value={winRate} />
        <TooltipItem label="Risk/Reward" value={d.riskReward || '—'} />
      </TooltipGrid>
      <hr className="my-2" />
      <TooltipGrid>
        <TooltipItem label="Max PnL" value={num(d.maxPnl)} />
        <TooltipItem label="Min PnL" value={num(d.minPnl)} />
        <TooltipItem label="Avg Profit" value={num(d.averageProfit)} />
        <TooltipItem label="Avg Loss" value={num(d.averageLoss)} />
        <TooltipItem label="Profit Trades" value={d.numberOfProfitTrades || 0} />
        <TooltipItem label="Loss Trades" value={d.numberOfLossTrades || 0} />
      </TooltipGrid>
      <hr className="my-2" />
      <TooltipGrid>
        <TooltipItem label="Avg Pips" value={num(d.averageInPips, 1)} />
        <TooltipItem label="Weighted Avg Pips" value={num(d.weightedAveragePips, 1)} />
        <TooltipItem label="Max Pips" value={num(d.maxInPips, 1)} />
        <TooltipItem label="Min Pips" value={num(d.minInPips, 1)} />
      </TooltipGrid>
      <hr className="my-2" />
      <TooltipGrid>
        <TooltipItem label="Avg Duration (days)" value={num(d.averageDays, 1)} />
        <TooltipItem label="Max Days" value={num(d.maxDays, 1)} />
        <TooltipItem label="Min Days" value={num(d.minDays, 1)} />
      </TooltipGrid>
    </>
  );
}

// --- Client login profile (renders from the already-loaded enrichment row) ---

export function ClientLoginInfoTooltip({ row }: { row: RiskDashboardClientAccountEnrichmentDto }) {
  return (
    <HoverCard
      width={440}
      title={<span>{row.login || '—'}</span>}
      body={
        <>
          <div className="mb-2 d-flex flex-wrap gap-1 align-items-center">
            <span className="fs-11 text-muted text-uppercase fw-semibold me-1">Profile</span>
            {row.isEa && <span className="fk-scalper-badge fk-ea-badge">EA</span>}
            {row.isScalper && <span className="fk-scalper-badge">SCALPER</span>}
            {!row.isEa && !row.isScalper && <span className="text-muted">Standard</span>}
          </div>
          <TooltipGrid>
            <TooltipItem label="Name" value={row.name || 'N/A'} />
            <TooltipItem label="Group" value={row.group || 'N/A'} />
            <TooltipItem label="Country" value={row.country || 'N/A'} />
            <TooltipItem label="Currency" value={row.accCurrency || 'N/A'} />
            <TooltipItem label="Unique ID" value={row.uniqueId || 'N/A'} />
            <TooltipItem label="Connector" value={row.connector || 'N/A'} />
          </TooltipGrid>
          <hr className="my-2" />
          <TooltipSection label="Metrics" />
          <TooltipGrid>
            <TooltipItem label="Net In/Out" value={num(row.netInOut)} />
            <TooltipItem label="Volume Closed" value={num(row.volumeClosed)} />
            <TooltipItem label="Risky Vol" value={num(row.riskyVol)} />
            <TooltipItem label="% Net PnL" value={row.percNetPnl != null ? `${num(row.percNetPnl)}%` : 'N/A'} />
            <TooltipItem label="Charges" value={num(row.charges)} />
            <TooltipItem label="Unrealized Δ" value={num(row.unrealizedVariation)} />
            <TooltipItem label="Max Drawdown" value={row.drawdown != null ? `${num(row.drawdown)}%` : 'TBD'} />
            <TooltipItem label="Avg Trade Time" value="TBD" />
          </TooltipGrid>
          <hr className="my-2" />
          <TooltipSection label="Cost Ratios" />
          <TooltipGrid>
            <TooltipItem label="Commission T/O" value={num(row.commissionTurnover)} />
            <TooltipItem label="Swap T/O" value={num(row.swapTurnover)} />
            <TooltipItem label="Cost T/O" value={num(row.costTurnover)} />
          </TooltipGrid>
        </>
      }
    >
      {InfoButton}
    </HoverCard>
  );
}

// --- LP account execution-quality (lazy fetch slippage summary by platformId) ---

export function LpLoginInfoTooltip({ row, dates }: { row: MergedLpRow; dates: { startDate: string; endDate: string } }) {
  return (
    <HoverCard
      width={440}
      title={<span>{row.login || '—'}</span>}
      body={<LpBody row={row} dates={dates} />}
    >
      {InfoButton}
    </HoverCard>
  );
}

function LpBody({ row, dates }: { row: MergedLpRow; dates: { startDate: string; endDate: string } }) {
  const platformId = row.platformId ?? 0;
  const q = useQuery({
    queryKey: ['rd-lp-slippage', platformId, dates.startDate, dates.endDate],
    queryFn: () =>
      riskDashboardApi.getLpSlippageSummary({
        startDate: dates.startDate,
        endDate: dates.endDate,
        dataSourceId: platformId,
      }),
    enabled: platformId > 0,
    staleTime: 60_000,
  });

  const s = q.data;
  const currency = s?.reportingCurrency || '';
  const fmtSlip = (v: number | null | undefined) => {
    if (v == null || Number.isNaN(v)) return 'N/A';
    return currency ? `${formatNumber(v, 5)} ${currency}` : formatNumber(v, 5);
  };
  const total =
    s == null
      ? null
      : s.totalSlippage != null
        ? s.totalSlippage
        : (s.totalPositive || 0) + (s.totalNegative || 0);
  const topInstrument = (s && (s.worstSymbol || s.bestSymbol)) || 'N/A';

  return (
    <>
      <TooltipSection label="Account" />
      <TooltipGrid>
        <TooltipItem label="Connector" value={row.connector || 'N/A'} />
        <TooltipItem label="Currency" value={currency || 'N/A'} />
        <TooltipItem label="Margin Level" value={row.marginLevel != null ? `${num(row.marginLevel)}%` : 'N/A'} />
      </TooltipGrid>
      <hr className="my-2" />
      <TooltipSection label="Execution Quality" />
      {q.isLoading || (platformId > 0 && !s && !q.isError) ? (
        <p className="text-muted mb-0 fs-12">
          <i className="fa fa-spinner fa-spin me-1" />
          Loading slippage…
        </p>
      ) : q.isError || platformId === 0 ? (
        <p className="text-muted mb-0 fs-12">Slippage summary unavailable.</p>
      ) : (
        <TooltipGrid>
          <TooltipItem label="Top Slippage" value={topInstrument} />
          <TooltipItem label="Total Slippage" value={fmtSlip(total)} />
          <TooltipItem label="Best Slippage" value={fmtSlip(s?.topPositiveSlippage)} />
          <TooltipItem label="Worst Slippage" value={fmtSlip(s?.topNegativeSlippage)} />
          <TooltipItem label="Best Symbol" value={s?.bestSymbol || 'N/A'} />
          <TooltipItem label="Worst Symbol" value={s?.worstSymbol || 'N/A'} />
        </TooltipGrid>
      )}
      <div className="mt-2">
        <a className="fs-12" href="/User/RiskMonitoring/LPAccounts#execution-quality">
          More details on LP Accounts <i className="ri-share-box-line" />
        </a>
      </div>
    </>
  );
}
